import type { TablesInsert } from '@/integrations/supabase/types';
import type { AddonSuggestion, AddonSuggestionType } from '@/types/addonSuggestions';
import { ADDON_RULE_VERSION, addonRuleId } from '@/utils/addonSuggestionRules';
import { breadcrumbs } from '@/telemetry/breadcrumbs';

/**
 * CORE-BP Slice B — what happened to each suggestion, and recording it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THE OUTCOME IS DERIVED IN ONE PLACE
 * ═══════════════════════════════════════════════════════════════════════════
 * The two handlers each built this payload themselves, and they disagreed. The
 * dismiss path recorded every shown suggestion as declined, correctly. The
 * accept path hardcoded `dismissed_keys: []` — so an advisor shown three
 * suggestions who accepted one produced a record saying one was accepted and
 * NOTHING was declined. Two suggestions vanished.
 *
 * That is not a reporting inconvenience. CORE-BP's sixth responsibility is that
 * a shop can "see whether its rules match its reality", and its eighth is that
 * "a suggestion the shop always dismisses is a defect in the rule". A rule that
 * is shown constantly and refused constantly looked, in the data, like a rule
 * nobody had an opinion about. The one signal that identifies a bad rule was
 * being discarded by the path advisors take most often.
 *
 * So the derivation lives here, once. Both handlers call it, and neither can
 * drift again.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DECLINING STAYS FREE
 * ═══════════════════════════════════════════════════════════════════════════
 * Recording a decline is not the same as asking for one. Nothing here requires
 * a reason, blocks an action, or treats a refusal as anything but an ordinary
 * answer — responsibility 4 is unchanged. The shop learns which of ITS RULES
 * are wrong; nobody learns anything about the person who declined.
 */

/** Every shown suggestion, partitioned. `accepted ∪ declined === shown`. */
export interface SuggestionOutcome {
  shown: AddonSuggestionType[];
  accepted: AddonSuggestionType[];
  declined: AddonSuggestionType[];
}

/** Order-preserving de-duplication; the display order is the reporting order. */
function distinct(types: readonly AddonSuggestionType[]): AddonSuggestionType[] {
  return types.filter((t, i, a) => a.indexOf(t) === i);
}

/**
 * The one derivation.
 *
 * `declined` is defined as **shown minus accepted**, which is what makes the
 * three sets reconcile by construction rather than by the caller remembering
 * to. Dismissing everything and accepting nothing produce the same record,
 * because they are the same event: the advisor was shown work and took none of
 * it.
 *
 * An accepted type that was never shown is ignored rather than trusted. It
 * cannot be declined either, so admitting it would break the reconciliation the
 * whole capability reports on.
 */
export function deriveSuggestionOutcome(
  shown: readonly AddonSuggestion[] | readonly AddonSuggestionType[],
  accepted: readonly AddonSuggestionType[] = [],
): SuggestionOutcome {
  const shownTypes = distinct(
    (shown as readonly (AddonSuggestion | AddonSuggestionType)[]).map((s) =>
      typeof s === 'string' ? s : s.type),
  );
  const acceptedSet = new Set(accepted);
  const acceptedTypes = shownTypes.filter((t) => acceptedSet.has(t));
  const declinedTypes = shownTypes.filter((t) => !acceptedSet.has(t));
  return { shown: shownTypes, accepted: acceptedTypes, declined: declinedTypes };
}

/* ═══════════════════════════════ the write ════════════════════════════════ */

/** The generated row type. The compiler now defends this shape. */
export type AddonSuggestionEventRow = TablesInsert<'addon_suggestion_events'>;

/** One shown suggestion's provenance as recorded (`suggestion_reasons[type]`). */
export interface RecordedSuggestionReason {
  rule_id: string;
  rule_version: number;
  reason: string;
  policy_sources: string[];
}

/**
 * Core-100 R4 — CORE-BP "Provenance / Explainability": the technical reason and the rule that produced
 * each suggestion are recorded WITH the outcome, beside the trigger that matched. A suggestion built
 * without provenance (an older caller) still records its reason and its rule identity; its policy
 * sources are then honestly empty rather than guessed. The database refuses a new row whose shown
 * suggestion has no reason (CHECK addon_suggestion_events_reason_recorded).
 */
export function buildSuggestionReasons(
  suggestions: readonly AddonSuggestion[],
): Record<string, RecordedSuggestionReason> {
  const out: Record<string, RecordedSuggestionReason> = {};
  for (const s of suggestions) {
    if (out[s.type]) continue;
    out[s.type] = {
      rule_id: s.provenance?.ruleId ?? addonRuleId(s.type),
      rule_version: s.provenance?.ruleVersion ?? ADDON_RULE_VERSION,
      reason: s.reason,
      policy_sources: [...(s.provenance?.policySources ?? [])],
    };
  }
  return out;
}

/**
 * CORE-BP item 9 — there is deliberately no person here, and no ticket either.
 *
 * This carried `userId` and an optional `ticketId` until Slice C. Neither was
 * ever read by anything: the aggregation selects only `shop_id` and the three
 * outcome arrays, and no caller ever passed a ticket id. What they DID provide
 * was a per-advisor acceptance rate, one GROUP BY away, for any manager who
 * could read the raw table — the measure item 9 forbids.
 *
 * They are gone from the context, from the row, and from the table. The client
 * cannot attribute a suggestion outcome to a person because it is no longer
 * given the means to.
 */
export interface AddonEventContext {
  shopId: string;
  vehicle: { year?: number | null; make?: string | null; model?: string | null };
  jobNames: string[];
  suggestions: readonly AddonSuggestion[];
  accepted?: readonly AddonSuggestionType[];
}

/**
 * Build the row. Pure, so the payload can be asserted without a database.
 *
 * Typed as the generated `TablesInsert` rather than cast through `as any`,
 * which is how the two handlers were able to send different shapes for the same
 * event without anything complaining.
 */
export function buildAddonSuggestionEventRow(
  ctx: AddonEventContext,
): AddonSuggestionEventRow {
  const outcome = deriveSuggestionOutcome(ctx.suggestions, ctx.accepted ?? []);
  return {
    shop_id: ctx.shopId,
    vehicle_year: ctx.vehicle.year ?? null,
    vehicle_make: ctx.vehicle.make ?? null,
    vehicle_model: ctx.vehicle.model ?? null,
    job_names: ctx.jobNames,
    suggestions_shown: outcome.shown,
    suggestions_accepted: outcome.accepted,
    dismissed_keys: outcome.declined,
    matched_triggers: Object.fromEntries(
      ctx.suggestions.map((s) => [s.type, s.matchedTriggers]),
    ),
    suggestion_reasons: buildSuggestionReasons(ctx.suggestions) as unknown as AddonSuggestionEventRow['suggestion_reasons'],
  };
}

/** The narrow slice of the Supabase client this needs, so tests need no server. */
export interface AddonEventWriter {
  from(table: 'addon_suggestion_events'): {
    insert(row: AddonSuggestionEventRow): PromiseLike<{ error: unknown }>;
  };
}

/**
 * Record the outcome WITHOUT making anyone wait for it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * OFF THE CRITICAL PATH, BUT NOT INTO THE VOID
 * ═══════════════════════════════════════════════════════════════════════════
 * This returns `void` and is never awaited: an advisor who has just decided
 * what work to do should move straight to placing it, and a slow or broken
 * telemetry write must not stand between them and their next step. Previously
 * both handlers awaited this insert — and an unauthenticated `getUser()` call
 * before it — so a slow network delayed the workflow and a failure was
 * invisible.
 *
 * "Not awaited" is not the same as "unobserved", and the difference is the
 * whole point:
 *
 *   · the promise is always terminated with a `.catch`, so a rejection can
 *     never surface as an unhandled rejection;
 *   · a returned Supabase `error` is caught too — that path resolves rather
 *     than rejects, and is the failure a `try/catch` would silently miss;
 *   · either way a breadcrumb is emitted, so the write's absence is
 *     discoverable instead of merely being absent.
 *
 * No queue, no retry, no durable buffer. This is one telemetry row about a
 * decision the human has already made; losing it must cost the shop a data
 * point and never cost the advisor their place in the workflow.
 */
export function recordAddonSuggestionEvent(
  client: AddonEventWriter,
  ctx: AddonEventContext,
): void {
  let row: AddonSuggestionEventRow;
  try {
    row = buildAddonSuggestionEventRow(ctx);
  } catch (e) {
    // A malformed context must not throw into the caller's handler either.
    breadcrumbs.log('OB:GUARD', {
      action: 'addon_suggestion_event_not_built',
      reason: e instanceof Error ? e.message : 'unknown',
    });
    return;
  }

  // The call itself is guarded, not just the promise. A client that throws
  // SYNCHRONOUSLY — a misconfigured or torn-down instance — would otherwise
  // escape into the UI handler and interrupt an advisor mid-workflow, which is
  // the exact failure this function exists to prevent. Caught in testing, not
  // reasoned about.
  let pending: PromiseLike<{ error: unknown }>;
  try {
    pending = client.from('addon_suggestion_events').insert(row);
  } catch (e) {
    breadcrumbs.log('OB:FAIL', {
      action: 'addon_suggestion_event_write_threw',
      reason: e instanceof Error ? e.message : String(e),
      shopId: ctx.shopId,
    });
    return;
  }

  void Promise.resolve(pending)
    .then((result) => {
      const error = result?.error;
      if (error) {
        breadcrumbs.log('OB:FAIL', {
          action: 'addon_suggestion_event_write_failed',
          reason: (error as { message?: string })?.message ?? String(error),
          shopId: ctx.shopId,
        });
      }
    })
    .catch((e: unknown) => {
      breadcrumbs.log('OB:FAIL', {
        action: 'addon_suggestion_event_write_threw',
        reason: e instanceof Error ? e.message : String(e),
        shopId: ctx.shopId,
      });
    });
}
